import { api, toList } from '../apiClient';

const multipart = (file) => {
  const f = new FormData();
  f.append('file', file);
  return { data: f, headers: { 'Content-Type': 'multipart/form-data' } };
};

export const businessService = {
  // ── Public ──────────────────────────────────────────────────────────────────
  suggest:    (q, limit = 8) => api.get('/businesses/suggest', { params: { q, limit } }).then(r => r?.data ?? r ?? []),
  getAll:     (params = {}) => api.get('/businesses', { params }).then(toList),
  getFeatured: ()           => api.get('/businesses/featured').then(toList),
  getRecent:  (size = 12)   => api.get('/businesses/recent', { params: { size } }).then(toList),
  search:     (params = {}) => api.get('/businesses/search', { params }).then(toList),
  getNearby:  (params = {}) => api.get('/businesses/nearby', { params }).then(toList),
  getById:    (id)          => api.get(`/businesses/${id}`),
  getBySlug:  (slug)        => api.get(`/businesses/slug/${slug}`),
  getReviews: (id, params)  => api.get(`/businesses/${id}/reviews`, { params }).then(toList),
  share:      (id)          => api.post(`/businesses/${id}/share`),
  getFollowStats: (id)      => api.get(`/businesses/${id}/follow/stats`),

  // ── Authenticated ───────────────────────────────────────────────────────────
  getMy:    (params = {}) => api.get('/businesses/my', { params }).then(toList),
  isSaved:  (id)          => api.get(`/businesses/${id}/saved`),
  save:     (id)          => api.post(`/businesses/${id}/save`),
  unsave:   (id)          => api.delete(`/businesses/${id}/save`),

  // ── Owner / Admin ────────────────────────────────────────────────────────────
  create:     (data)         => api.post('/businesses', data),
  update:     (id, data)     => api.put(`/businesses/${id}`, data),
  delete:     (id)           => api.delete(`/businesses/${id}`),
  generateQr: (id)           => api.post(`/businesses/${id}/qr`),
  updateRooms:(id, rooms)    => api.put(`/businesses/${id}/rooms`, rooms),
  updateMenu: (id, sections) => api.put(`/businesses/${id}/menu`, sections),
  getMenu:    (id, day)      => api.get(`/businesses/${id}/menu`, { params: day ? { day } : {} }),
  getRssFeed: (id)           => api.get(`/businesses/${id}/rss-feed`).then(r => Array.isArray(r) ? r : []),

  // ── Admin-only ──────────────────────────────────────────────────────────────
  adminGetAll: (params = {}) => api.get('/admin/businesses', { params: Object.fromEntries(Object.entries(params).filter(([,v]) => v !== undefined && v !== null && v !== '')) }).then(toList),
  /** POST /admin/businesses/backfill-coordinates — geocode businesses missing coords (SUPER_ADMIN).
   *  Returns { candidatesRemaining, processed, pinned, failed, lastProcessedId, dryRun, samples }. */
  backfillCoordinates: ({ afterId = 0, limit = 50, dryRun = true } = {}) =>
    api.post('/admin/businesses/backfill-coordinates', null, { params: { afterId, limit, dryRun } }),
  adminVerify:  (id, verified, reason) => api.put(`/admin/businesses/${id}/verify`, null, { params: { verified, ...(reason ? { reason } : {}) } }),
  adminFeature: (id, featured) => api.put(`/admin/businesses/${id}/feature`, null, { params: { featured } }),
  /** POST /admin/businesses/{id}/feature-as-new — reset created/updated to now (SUPER_ADMIN). */
  adminFeatureAsNew: (id) => api.post(`/admin/businesses/${id}/feature-as-new`),
  adminSetClaimed: (id, claimed) => api.put(`/admin/businesses/${id}/claimed`, null, { params: { claimed } }),
  adminStatus:  (id, status, reason) => api.put(`/admin/businesses/${id}/status`, null, { params: { status, ...(reason ? { reason } : {}) } }),
  adminReassignOwner: (businessId, userId) => api.put(`/admin/businesses/${businessId}/owner`, null, { params: { userId } }),
  adminSoftDelete: (id, reason) => api.delete(`/admin/businesses/${id}`, { params: reason ? { reason } : {} }),
  adminPurge:      (id) => api.delete(`/admin/businesses/${id}/purge`),
  /** Clears the provenance flag (e.g. after reviewing an imported listing). SUPER_ADMIN. */
  adminClearSource: (id) => api.put(`/admin/businesses/${id}/clear-source`),
  adminImportCsv: (file, source) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/businesses/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: source ? { source } : {},
    });
  },

  uploadLogo: (id, file) => {
    const { data, headers } = multipart(file);
    return api.post(`/businesses/${id}/logo`, data, { headers });
  },
  uploadCover: (id, file) => {
    const { data, headers } = multipart(file);
    return api.post(`/businesses/${id}/cover`, data, { headers });
  },
  uploadGallery: (id, file) => {
    const { data, headers } = multipart(file);
    return api.post(`/businesses/${id}/gallery`, data, { headers });
  },
  removeGalleryImage: (id, imageUrl) =>
    api.delete(`/businesses/${id}/gallery`, { params: { imageUrl } }),

  uploadMenuImage: (id, file) => {
    const { data, headers } = multipart(file);
    return api.post(`/businesses/${id}/menu-image`, data, { headers });
  },
  deleteMenuImage: (id, imageUrl) =>
    api.delete(`/businesses/${id}/menu-image`, { params: { imageUrl } }),
};
